import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Copy, Filter, Pencil, Settings, Trash2, X } from "lucide-react"
import { useSWRConfig } from "swr"
import { DeleteSmithersDeviceDialog } from "@/components/smithers/delete-smithers-device-dialog"
import { SmithersBulkUpdateBar } from "@/components/smithers/smithers-bulk-update-bar"
import { useToast } from "@/hooks/use-toast"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { DeviceSearchBar } from "@/components/alfred/device-search-bar"
import { ColumnsToggle } from "@/components/shared/columns-toggle"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { ApiErrorState, ClientNotFoundState, NoDevicesEmpty, NoSearchResultsEmpty } from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { SortableHeader, type SortDirection } from "@/components/shared/sortable-header"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useSmithersClients } from "@/hooks/use-smithers-clients"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useSmithersDevices, useDeleteSmithersDevice, useBulkDeleteSmithersDevices, useBulkUpdateSmithersDevices } from "@/hooks/use-smithers-devices"
import { cn, formatDateTimeWithZone } from "@/lib/utils"
import { getSmithersProVenueSelection } from "@/lib/smithers-provenue"
import { getSmithersPageAfterBulkDelete } from "@/lib/smithers-bulk"
import { getSmithersDeviceListHistoryState, SMITHERS_DEVICE_PAGE_SIZES } from "@/lib/smithers-device-navigation"
import { getSmithersClientListHistoryState } from "@/lib/smithers-client-navigation"
import type { SmithersBulkUpdatePatch, SmithersDeviceConfig } from "@/types/smithers-device"

import { loadSmithersDeviceColumns, saveSmithersDeviceColumns, SMITHERS_DEVICE_COLUMNS, SMITHERS_OPTIONAL_COLUMNS, type SmithersDeviceColumn } from "@/lib/smithers-device-columns"

type SortableColumn = Exclude<SmithersDeviceColumn, "id" | "proVenueInstances">

export function SmithersDevicesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { mutate: mutateCache } = useSWRConfig()
  const { toast } = useToast()
  const deletion = useDeleteSmithersDevice()
  const bulkDeletion = useBulkDeleteSmithersDevices()
  const bulkUpdate = useBulkUpdateSmithersDevices()
  const [bulkDeletePending, setBulkDeletePending] = useState(false)
  const [bulkUpdatePending, setBulkUpdatePending] = useState(false)
  const [bulkEditorScope, setBulkEditorScope] = useState<string>()
  const [bulkUpdateError, setBulkUpdateError] = useState<{ scope: string; message: string }>()
  const isMutating = deletion.isMutating || bulkDeletePending || bulkUpdatePending
  const mutating = useRef(false)
  const [pendingDelete, setPendingDelete] = useState<SmithersDeviceConfig>()
  const [deleteError, setDeleteError] = useState<string>()
  const [pendingBulkDelete, setPendingBulkDelete] = useState<{ scope: string; devices: SmithersDeviceConfig[]; page: number; pageSize: number; total: number }>()
  const [bulkDeleteError, setBulkDeleteError] = useState<string>()
  const [bulkFailures, setBulkFailures] = useState<{ scope: string; action: "deleted" | "updated"; items: { id: number; deviceId: string; clientId: string; error: string }[] }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [clientFilterOpen, setClientFilterOpen] = useState(false)
  const clientId = searchParams.get("clientId")
  const historyState = getSmithersDeviceListHistoryState(location.state)
  const restored = historyState?.clientId === clientId ? historyState : undefined
  const [clientListOrigin] = useState(() => restored?.clientList ?? getSmithersClientListHistoryState(location.state))
  const clientListState = clientListOrigin ? { clientList: clientListOrigin } : {}
  const backToClients = () => navigate("/smithers/clients", clientListOrigin ? { state: { smithersClientList: clientListOrigin } } : undefined)
  const [searchInput, setSearchInput] = useState(() => restored?.searchInput ?? "")
  const [filter, setFilter] = useState(() => restored?.filter ?? "")
  const [sortBy, setSortBy] = useState<SortableColumn>(() => restored?.sortBy ?? "deviceId")
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => restored?.sortDirection ?? "asc")
  const [visibleColumns, setVisibleColumns] = useState(loadSmithersDeviceColumns)
  const columns = SMITHERS_DEVICE_COLUMNS.filter(({ key }) => key === "deviceId" || key === "clientId" || visibleColumns.has(key))
  const [page, setPage] = useState(() => restored?.page ?? 1)
  const [pageSize, setPageSize] = useState(() => restored?.pageSize ?? 50)
  const pendingClient = useRef<{ clientId: string | null } | undefined>(undefined)
  useEffect(() => {
    // Wait for the new query before persisting controls to avoid restoring the old client.
    if (pendingClient.current && pendingClient.current.clientId !== clientId) return
    pendingClient.current = undefined
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
      replace: true,
      state: { smithersDeviceList: { clientId, page, pageSize, searchInput, filter, sortBy, sortDirection, ...(clientListOrigin ? { clientList: clientListOrigin } : {}) } },
    })
  }, [navigate, location.pathname, location.search, location.hash, clientId, page, pageSize, searchInput, filter, sortBy, sortDirection, clientListOrigin])
  const listOrigin = { fromSmithersDeviceList: true, smithersDeviceList: { clientId, page, pageSize, searchInput, filter, sortBy, sortDirection, ...clientListState } }
  const scope = JSON.stringify([clientId, page, pageSize, filter, sortBy, sortDirection])
  const currentScope = useRef(scope)
  useLayoutEffect(() => { currentScope.current = scope }, [scope])
  const [selection, setSelection] = useState(() => ({ scope, ids: new Set<number>() }))
  if (selection.scope !== scope) setSelection({ scope, ids: new Set<number>() })
  const clients = useSmithersClients()
  const selectedClient = clients.data?.find((client) => client.clientId === clientId)
  const clientPk = clientId === null ? undefined : selectedClient?.id ?? null
  const list = useSmithersDevices({ page, pageSize, filter, sortBy, sortOrder: sortDirection }, clientPk)
  const devices = list.data?.items ?? []
  const selectedIds = new Set(devices.filter((device) => selection.scope === scope && selection.ids.has(device.id)).map((device) => device.id))
  const allSelected = devices.length > 0 && selectedIds.size === devices.length
  const clearSelection = () => { setSelection({ scope, ids: new Set<number>() }); setBulkFailures(undefined); setBulkEditorScope(undefined); setBulkUpdateError(undefined) }
  const toggleSelection = (id: number, checked: boolean) => {
    setSelection((current) => {
      const ids = new Set(current.scope === scope ? current.ids : [])
      if (checked) ids.add(id)
      else ids.delete(id)
      return { scope, ids }
    })
  }
  const total = list.data?.total ?? 0
  const isLoading = list.isLoading || (clientId !== null && clients.isLoading)
  const tableRef = useRef<HTMLTableElement>(null)
  const sortScroll = useRef<number | undefined>(undefined)
  useLayoutEffect(() => {
    if (isLoading || sortScroll.current === undefined || !tableRef.current) return
    if (tableRef.current.parentElement) tableRef.current.parentElement.scrollLeft = sortScroll.current
    sortScroll.current = undefined
  }, [list.data?.items, isLoading])
  const error = list.error || (clientId !== null ? clients.error : undefined)
  const missingClient = clientId !== null && clients.data !== undefined && !selectedClient
  const mutate = () => clientId !== null && clients.error ? clients.mutate() : list.mutate()
  const clearSearch = () => { setSearchInput(""); setFilter(""); setPage(1) }
  const changeClient = (nextClientId: string | null) => {
    if (isMutating || mutating.current) return
    const params = new URLSearchParams(searchParams)
    if (nextClientId === null) params.delete("clientId")
    else params.set("clientId", nextClientId)
    pendingClient.current = { clientId: nextClientId }
    setClientFilterOpen(false)
    setPage(1)
    clearSelection()
    setSearchParams(params, { replace: true })
  }
  const clearFilters = () => { clearSearch(); changeClient(null) }
  const clientQuery = clientId === null ? "" : `?${new URLSearchParams({ clientId })}`
  const addDevice = () => { if (!isMutating) navigate(`/smithers/devices/new${clientQuery}`, { state: listOrigin }) }
  const onSort = (column: SortableColumn) => {
    sortScroll.current = tableRef.current?.parentElement?.scrollLeft ?? 0
    setPage(1)
    setSortBy(column)
    setSortDirection(column === sortBy && sortDirection === "asc" ? "desc" : "asc")
  }

  const confirmDelete = async () => {
    if (!pendingDelete || mutating.current) return
    mutating.current = true
    setDeleteError(undefined)
    try {
      await deletion.trigger(pendingDelete.id)
      toggleSelection(pendingDelete.id, false)
      await mutateCache((key) => Array.isArray(key) && key[0] === "smithers" && key[1] === "devices", undefined, { revalidate: true })
      if (devices.length === 1 && page > 1) setPage((current) => current - 1)
      setPendingDelete(undefined)
      toast({ variant: "success", title: "Device deleted" })
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to delete device"
      setDeleteError(message)
      toast({ variant: "error", title: "Failed to delete device", description: message })
    } finally { mutating.current = false }
  }

  const changeColumns = (next: Set<SmithersDeviceColumn>) => {
    if (isMutating) return
    setVisibleColumns(next)
    saveSmithersDeviceColumns(next)
    if (sortBy !== "deviceId" && sortBy !== "clientId" && !next.has(sortBy)) {
      setPage(1)
      setSortBy("deviceId")
      setSortDirection("asc")
    }
  }

  const confirmBulkDelete = async () => {
    if (!pendingBulkDelete || pendingBulkDelete.scope !== scope || mutating.current) return
    const submitted = pendingBulkDelete
    mutating.current = true
    setBulkDeletePending(true)
    setBulkDeleteError(undefined)
    try {
      const result = await bulkDeletion.trigger(submitted.devices.map((device) => device.id))
      const failed = result.results.filter((item) => item.status === "failed")
      setSelection((current) => current.scope === submitted.scope ? { ...current, ids: new Set(failed.map((item) => item.id)) } : current)
      setBulkFailures({ scope: submitted.scope, action: "deleted", items: failed.map((item) => {
        const device = submitted.devices.find((device) => device.id === item.id)
        return { id: item.id, deviceId: device?.deviceId ?? String(item.id), clientId: device?.clientId ?? "", error: item.error || "Unable to delete device" }
      }) })
      setPendingBulkDelete(undefined)
      if (currentScope.current === submitted.scope) setPage(getSmithersPageAfterBulkDelete(submitted.page, submitted.total, result.deleted, submitted.pageSize))
      toast({
        variant: result.failed === 0 ? "success" : result.deleted > 0 ? "warning" : "error",
        title: result.failed === 0 ? `${result.deleted} device${result.deleted === 1 ? "" : "s"} deleted` : `${result.deleted} deleted, ${result.failed} failed`,
        description: result.failed > 0 ? "Failed devices still in the list remain selected for retry." : undefined,
      })
      await mutateCache((key) => Array.isArray(key) && key[0] === "smithers" && key[1] === "devices", undefined, { revalidate: true }).catch(() => undefined)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to delete devices"
      if (currentScope.current === submitted.scope) {
        setBulkDeleteError(message)
        toast({ variant: "error", title: "Failed to delete devices", description: message })
      }
    } finally { mutating.current = false; setBulkDeletePending(false) }
  }

  const saveBulkUpdate = async (patch: SmithersBulkUpdatePatch) => {
    if (selectedIds.size === 0 || bulkEditorScope !== scope || mutating.current) return
    const submittedScope = scope
    const submittedDevices = devices.filter((device) => selectedIds.has(device.id))
    mutating.current = true
    setBulkUpdatePending(true)
    setBulkUpdateError(undefined)
    try {
      const result = await bulkUpdate.trigger({ ids: submittedDevices.map((device) => device.id), update: patch })
      setSelection((current) => current.scope === submittedScope ? { ...current, ids: new Set(result.failedDevices.map((item) => item.id)) } : current)
      setBulkFailures({ scope: submittedScope, action: "updated", items: result.failedDevices.map((item) => {
        const device = submittedDevices.find((device) => device.id === item.id)
        return { id: item.id, deviceId: device?.deviceId ?? String(item.id), clientId: device?.clientId ?? "", error: item.message }
      }) })
      if (result.failureCount === 0) setBulkEditorScope((current) => current === submittedScope ? undefined : current)
      toast({ variant: result.failureCount === 0 ? "success" : result.successCount > 0 ? "warning" : "error", title: `${result.successCount} of ${result.requestedCount} devices updated`, description: result.failureCount > 0 ? "Failed devices still in the list remain selected for retry." : "Configuration changes have been applied." })
      await mutateCache((key) => Array.isArray(key) && key[0] === "smithers" && key[1] === "devices").catch(() => undefined)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to update devices"
      if (currentScope.current === submittedScope) {
        setBulkUpdateError({ scope: submittedScope, message })
        toast({ variant: "error", title: "Failed to update devices", description: message })
      }
    } finally { mutating.current = false; setBulkUpdatePending(false) }
  }

  return (
    <>
      <DashboardHeader onAddDevice={addDevice} onBulkAdd={() => { if (!isMutating) navigate(`/smithers/devices/bulk-add${clientQuery}`, { state: listOrigin }) }} />
      <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div>
          {clientId !== null && (
            <Button
              variant="ghost"
              size="sm"
              onClick={backToClients}
              className="text-muted-foreground hover:text-foreground hover:bg-secondary -ml-2 mb-4"
            >
              <ArrowLeft data-icon="inline-start" className="mr-1" />
              Back to Clients
            </Button>
          )}
          <h1 className="mb-2 text-2xl font-semibold text-foreground">Smithers Devices</h1>
          <p className="text-sm text-muted-foreground">{clientId === null ? "Device configuration for Smithers." : `Devices for client ${clientId}.`}</p>
        </div>
        {import.meta.env.VITE_USE_MOCKS === "true" && <Alert>
          <AlertTitle>Sample device data</AlertTitle>
          <AlertDescription>Mock mode is enabled. This list shows sample devices. Values marked (default) are inherited from global configuration.</AlertDescription>
        </Alert>}
        <div className="flex flex-col gap-3">
          <DeviceSearchBar
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() => { setFilter(searchInput.trim()); setPage(1) }}
            onClear={clearSearch}
            activeFilter={filter}
            disabled={isMutating}
            placeholder="Search devices by ID, SOTI ID, client, venue, area..."
          />
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-2">
              <Popover open={clientFilterOpen} onOpenChange={setClientFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" disabled={isMutating} className="border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground">
                    <Filter data-icon="inline-start" />Filter by Client<ChevronDown data-icon="inline-end" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 max-w-[calc(100vw-2rem)] border-border bg-popover p-2" aria-label="Filter devices by client">
                  <p className="px-2 pb-2 text-xs text-muted-foreground">Show devices for a client</p>
                  {clients.isLoading ? <p role="status" className="px-2 py-1.5 text-sm text-muted-foreground">Loading clients…</p>
                    : clients.error ? <div className="flex flex-col gap-2 px-2 py-1.5"><p role="alert" className="text-sm text-destructive">Couldn’t load clients.</p><Button size="sm" variant="outline" onClick={() => void clients.mutate()}>Retry clients</Button></div>
                    : clients.data?.length === 0 ? <p className="px-2 py-1.5 text-sm text-muted-foreground">No clients available.</p>
                    : <RadioGroup value={clientId ?? ""} onValueChange={changeClient} disabled={isMutating} aria-label="Client" className="max-h-72 gap-0 overflow-y-auto overflow-x-hidden">
                      {clients.data?.map((client) => (
                        <Label key={client.id} htmlFor={`smithers-filter-client-${client.id}`} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 font-normal hover:bg-secondary">
                          <RadioGroupItem value={client.clientId} id={`smithers-filter-client-${client.id}`} className="shrink-0" />
                          <span className="min-w-0 break-all">{client.clientId}</span>
                        </Label>
                      ))}
                    </RadioGroup>}
                </PopoverContent>
              </Popover>
              {clientId !== null && <Badge variant="secondary" className="w-fit gap-1 pr-1">
                <span className="max-w-[16rem] truncate">Client: {clientId}</span>
                <button type="button" onClick={() => changeClient(null)} disabled={isMutating} aria-label="Clear client filter" className="rounded-full p-0.5 hover:bg-background/60"><X className="size-3" /></button>
              </Badge>}
            </div>
            <ColumnsToggle columns={SMITHERS_OPTIONAL_COLUMNS} visibleColumns={visibleColumns} onChange={changeColumns} />
          </div>
        </div>
        {bulkFailures?.scope === scope && bulkFailures.items.length > 0 && (
          <Alert variant="destructive">
            <AlertTitle>Some devices could not be {bulkFailures.action}</AlertTitle>
            <AlertDescription>
              <p>Failed devices still in the list remain selected for retry.</p>
              <ul className="mt-2 max-h-60 overflow-y-auto">
                {bulkFailures.items.map((item) => <li key={item.id} className="break-all">{item.deviceId} ({item.clientId}): {item.error}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        {isLoading ? <DevicesTableSkeleton /> : error ? <ApiErrorState onRetry={() => void mutate()} /> : missingClient ? <ClientNotFoundState onAction={backToClients} /> : devices.length === 0 ? (
          filter ? <NoSearchResultsEmpty onAction={clearFilters} /> : <NoDevicesEmpty onAction={addDevice} />
        ) : (
          <Card>
            <CardContent className="p-0">
              {selectedIds.size > 0 && bulkEditorScope === scope ? (
                <SmithersBulkUpdateBar key={scope} selectedCount={selectedIds.size} onSave={saveBulkUpdate} onCancel={() => { setBulkEditorScope(undefined); setBulkUpdateError(undefined) }} isSaving={isMutating} error={bulkUpdateError?.scope === scope ? bulkUpdateError.message : undefined} />
              ) : selectedIds.size > 0 && (
                <div className="border-b border-border px-4 py-2.5">
                  <div role="toolbar" aria-label="Bulk device actions" className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span aria-live="polite" className="inline-flex items-center rounded-md bg-muted px-2.5 py-1 text-sm font-medium text-foreground">
                        {selectedIds.size} device{selectedIds.size === 1 ? "" : "s"} selected
                      </span>
                      <Button type="button" size="sm" variant="secondary" disabled={isMutating} onClick={() => { setBulkUpdateError(undefined); setBulkEditorScope(scope) }}>
                        <Settings data-icon="inline-start" />
                        Bulk Update
                      </Button>
                      <Button type="button" size="sm" variant="destructive" disabled={isMutating} onClick={() => { setBulkDeleteError(undefined); setPendingBulkDelete({ scope, devices: devices.filter((device) => selectedIds.has(device.id)), page, pageSize, total }) }}>
                        <Trash2 data-icon="inline-start" />
                        Bulk Delete
                      </Button>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={clearSelection} disabled={isMutating}>
                      <X data-icon="inline-start" />
                      Clear selection
                    </Button>
                  </div>
                </div>
              )}
              <Table ref={tableRef} className="min-w-max w-full" aria-label="Smithers devices">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 pl-4">
                      <Checkbox
                        aria-label="Select all devices on this page"
                        checked={allSelected ? true : selectedIds.size > 0 ? "indeterminate" : false}
                        onCheckedChange={(checked) => setSelection({ scope, ids: new Set(checked === true ? devices.map((device) => device.id) : []) })}
                        disabled={isMutating}
                        className="border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                      />
                    </TableHead>
                    {columns.map(({ key, label }) => (
                      key === "id" || key === "proVenueInstances"
                        ? <TableHead key={key}>{label}</TableHead>
                        : <SortableHeader key={key} label={label} sortKey={key} currentSortColumn={sortBy} currentSortDirection={sortDirection} onSort={onSort} disabled={isMutating} />
                    ))}
                    <TableHead className="sticky right-0 z-10 w-24 bg-card font-medium text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device, index) => (
                    <TableRow
                      key={device.id}
                      tabIndex={isMutating ? -1 : 0}
                      aria-disabled={isMutating}
                      className={cn("border-border cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring", index % 2 === 1 && "bg-muted/30 hover:bg-muted/50", selectedIds.has(device.id) && "bg-primary/10 hover:bg-primary/15")}
                      onClick={() => {
                        if (!isMutating) navigate(`/smithers/devices/${device.id}${clientQuery}`, { state: listOrigin })
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && event.target === event.currentTarget && !isMutating) {
                          event.preventDefault()
                          navigate(`/smithers/devices/${device.id}${clientQuery}`, { state: listOrigin })
                        }
                      }}
                    >
                      <TableCell className="w-10 pl-4" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${device.deviceId}`}
                          checked={selectedIds.has(device.id)}
                          onCheckedChange={(checked) => toggleSelection(device.id, checked === true)}
                          disabled={isMutating}
                          className="border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                        />
                      </TableCell>
                      {columns.map(({ key }) => (
                        <TableCell key={key} className={key === "deviceId" ? "font-medium" : undefined}>
                          <DeviceCell device={device} column={key} />
                        </TableCell>
                      ))}
                      <TableCell className="sticky right-0 z-10 bg-card" onClick={(event) => event.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" disabled={isMutating} onClick={() => navigate(`/smithers/devices/${device.id}/edit${clientQuery}`, { state: listOrigin })} className="size-8 text-muted-foreground hover:bg-secondary hover:text-foreground"><Pencil className="size-4" /><span className="sr-only">Edit device {device.deviceId}</span></Button>
                          <Button variant="ghost" size="icon" disabled={isMutating} onClick={() => navigate(`/smithers/devices/new?${new URLSearchParams({ copyFrom: String(device.id), ...(clientId === null ? {} : { clientId }) })}`, { state: listOrigin })} className="size-8 text-muted-foreground hover:bg-secondary hover:text-foreground"><Copy className="size-4" /><span className="sr-only">Copy device {device.deviceId}</span></Button>
                          <Button variant="ghost" size="icon" disabled={isMutating} onClick={() => { setDeleteError(undefined); setPendingDelete(device) }} className="size-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="size-4" /><span className="sr-only">Delete device {device.deviceId}</span></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
                <p className="text-sm text-muted-foreground" role="status">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} devices</p>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="smithers-page-size" className="whitespace-nowrap text-sm text-muted-foreground">Rows per page</Label>
                    <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1) }} disabled={isMutating}>
                      <SelectTrigger id="smithers-page-size" className="h-8 w-20 border-border bg-card text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectGroup>{SMITHERS_DEVICE_PAGE_SIZES.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}</SelectGroup></SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" aria-label="Previous page" disabled={page === 1 || isMutating} onClick={() => setPage((current) => current - 1)}><ChevronLeft data-icon="inline-start" />Previous</Button>
                    <span className="text-sm text-muted-foreground">Page {page}</span>
                    <Button variant="outline" size="sm" aria-label="Next page" disabled={page * pageSize >= total || isMutating} onClick={() => setPage((current) => current + 1)}>Next<ChevronRight data-icon="inline-end" /></Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <DeleteSmithersDeviceDialog open={pendingDelete !== undefined} onOpenChange={(open) => { if (!open) setPendingDelete(undefined) }} devices={pendingDelete ? [pendingDelete] : []} onConfirm={() => void confirmDelete()} isLoading={isMutating} error={deleteError} />
      <DeleteSmithersDeviceDialog open={pendingBulkDelete?.scope === scope} onOpenChange={(open) => { if (!open) setPendingBulkDelete(undefined) }} devices={pendingBulkDelete?.devices ?? []} onConfirm={() => void confirmBulkDelete()} isLoading={isMutating} error={bulkDeleteError} />
    </>
  )
}

function ProVenueCell({ device }: { device: SmithersDeviceConfig }) {
  const selection = getSmithersProVenueSelection(device)
  return (
    <div className="flex items-center gap-2">
      {device.proVenueInstanceId === null ? "None" : `${device.proVenueUser || "Instance"} (${device.proVenueInstanceId})`}
      {selection.mode === "global" && <span className="text-xs text-muted-foreground">(default)</span>}
    </div>
  )
}

function DeviceCell({ device, column }: { device: SmithersDeviceConfig; column: SmithersDeviceColumn }) {
  if (column === "proVenueUser") return <ProVenueCell device={device} />
  if (column === "proVenueInstances") return device.proVenueInstances[0]?.hostName || "—"
  if (column === "createdAt" || column === "updatedAt") return formatDateTimeWithZone(device[column])
  return (
    <div className="flex items-center gap-2">
      {device[column] === "" ? "—" : device[column]}
      {device.inheritedFromGlobal.includes(column) && <span className="text-xs text-muted-foreground">(default)</span>}
    </div>
  )
}
